<div class="container h-full flex flex-col gap-4 mx-auto px-4 md:px-6 pt-8 pb-4">
  <div class="flex flex-wrap items-center gap-2 min-h-10">
    <h2 class="text-2xl font-medium">Audits</h2>
  </div>

  <div class="bg-white overflow-hidden rounded-lg min-h-0 flex flex-col">
    <div class="flex flex-wrap items-center gap-2 p-4">
      <mat-form-field
        class="w-60 [--mat-select-trigger-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
        subscriptSizing="dynamic"
      >
        <mat-select
          multiple
          [value]="selectedResource()"
          (selectionChange)="handleResourceChange($event)"
          placeholder="Resource"
        >
          @for (resource of resources(); track resource) {
            <mat-option [value]="resource">{{ resource }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field
        class="w-44 [--mat-select-trigger-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
        subscriptSizing="dynamic"
      >
        <mat-select
          multiple
          [value]="selectedAction()"
          (selectionChange)="handleActionChange($event)"
          placeholder="Actie"
        >
          @for (action of actions(); track action) {
            <mat-option [value]="action">{{ action }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field
        class="w-52 [--mat-select-trigger-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
        subscriptSizing="dynamic"
      >
        <mat-select
          multiple
          [value]="selectedUser()"
          (selectionChange)="handleUserChange($event)"
          placeholder="Gebruiker"
        >
          @for (user of users(); track user.userId) {
            <mat-option [value]="user.userId">{{
              user.displayName || user.userId
            }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <div class="flex flex-wrap items-center gap-2 ml-auto">
        <mat-chip-listbox
          (change)="handleDatePickerOptionChange($event)"
          hideSingleSelectionIndicator
          class="inline-flex! py-1 px-2 border border-gray-200 rounded-full [--mdc-chip-outline-color:transparent] [--mdc-chip-container-shape-radius:24px] [--mdc-chip-label-text-color:var(--color-gray-500)] [--mdc-chip-elevated-selected-container-color:var(--color-secondary-200)] [--mdc-chip-selected-label-text-color:var(--color-secondary-800)]"
        >
          @for (option of dateRangeOptions(); track $index) {
            <mat-chip-option
              [value]="option.value"
              [selected]="option.value === dateRangeOptionSelected()"
              [selectable]="option.value !== dateRangeOptionSelected()"
              [matTooltip]="option.description"
              >{{ option.value | uppercase }}</mat-chip-option
            >
          }
        </mat-chip-listbox>

        <div class="relative inline-block">
          <div
            class="inline-flex items-center gap-1 p-1 pl-4 border border-gray-200 rounded-full cursor-default"
          >
            <span class="text-sm text-gray-500 font-medium select-none">
              {{ formatDateRange() }}
            </span>

            <div class="relative">
              <mat-date-range-input
                [formGroup]="dateRangePicker"
                [rangePicker]="picker"
                [max]="maxDate"
                class="opacity-0 pointer-events-none absolute inset-0"
              >
                <input matStartDate formControlName="start" />
                <input matEndDate formControlName="end" />
              </mat-date-range-input>

              <button
                mat-icon-button
                (click)="picker.open()"
                class="inline-flex! items-center justify-center"
                matTooltip="Datumbereik kiezen"
              >
                <mat-icon class="size-5! text-xl! leading-none!"
                  >calendar_today</mat-icon
                >
              </button>
            </div>
          </div>

          <mat-date-range-picker #picker [xPosition]="'end'">
            <mat-datepicker-actions>
              <button mat-button matDatepickerCancel>Annuleren</button>
              <button
                mat-raised-button
                color="primary"
                matDatepickerApply
                (click)="onApplyClicked()"
              >
                Toepassen
              </button>
            </mat-datepicker-actions>
          </mat-date-range-picker>
        </div>
      </div>
    </div>

    <app-data-table-container>
      <table
        mat-table
        matSort
        matSortDirection="desc"
        matSortActive="created_at"
        matSortDisableClear
        [matSortDisabled]="hasNoData()"
        (matSortChange)="handleSortChange($event)"
        [dataSource]="dataSource()"
      >
        <ng-container matColumnDef="createdAt">
          <th mat-header-cell mat-sort-header="created_at" *matHeaderCellDef>Datum</th>
          <td mat-cell *matCellDef="let element">
            {{ element.createdAt | date: 'dd MMM yyyy, HH:mm' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="displayName">
          <th mat-header-cell mat-sort-header="metadata.display_name" *matHeaderCellDef>
            Gebruiker
          </th>
          <td mat-cell *matCellDef="let element">
            @if (element.metadata?.display_name) {
              <div class="inline-flex items-center gap-1">
                <span
                  class="max-w-52 truncate block"
                  [matTooltip]="element.metadata.display_name"
                  matTooltipPosition="after"
                >
                  {{ element.metadata.display_name }}
                </span>
                @if (element.metadata?.username) {
                  <span class="text-xs font-medium text-gray-400"
                    >#{{ element.metadata.username }}</span
                  >
                }
              </div>
            } @else {
              <span class="text-gray-400">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="resource">
          <th mat-header-cell mat-sort-header="resource" *matHeaderCellDef>Resource</th>
          <td mat-cell *matCellDef="let element">
            <div class="inline-flex items-center gap-1.5">
              <span
                class="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700"
              >
                {{ element.resource }}
              </span>
              @if (element.sensitive) {
                <span
                  class="inline-flex items-center justify-center text-amber-600 bg-amber-50 rounded-full p-1 border border-amber-200"
                  matTooltip="Gevoelige gegevens"
                >
                  <mat-icon class="size-3.5! text-sm! leading-none!">lock</mat-icon>
                </span>
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="action">
          <th mat-header-cell mat-sort-header="action" *matHeaderCellDef>Actie</th>
          <td mat-cell *matCellDef="let element">
            <span
              class="text-xs font-medium px-2.5 py-1 rounded-full"
              [ngClass]="{
                'bg-green-100 text-green-800': element.action === 'create',
                'bg-blue-100 text-blue-800': element.action === 'update',
                'bg-red-100 text-red-800':
                  element.action === 'delete' || element.action === 'hard_delete',
                'bg-yellow-100 text-yellow-800': element.action === 'schedule_delete',
                'bg-purple-100 text-purple-800': element.action === 'restore',
                'bg-orange-100 text-orange-800':
                  element.action === 'attach' || element.action === 'detach',
                'bg-gray-100 text-gray-700': ![
                  'create',
                  'update',
                  'delete',
                  'hard_delete',
                  'schedule_delete',
                  'restore',
                  'attach',
                  'detach',
                ].includes(element.action),
              }"
            >
              {{ element.action }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let element">
            <span
              class="text-xs font-medium px-2.5 py-1 rounded-full"
              [ngClass]="{
                'bg-green-100 text-green-800': element.status === 'success',
                'bg-red-100 text-red-800': element.status === 'failure',
              }"
            >
              {{ statusMapping[element.status] || element.status }}
            </span>
          </td>
        </ng-container>

        <tr
          mat-header-row
          *matHeaderRowDef="displayedColumns; sticky: true"
          class="whitespace-nowrap"
        ></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="hover:bg-gray-100! whitespace-nowrap cursor-pointer"
          (click)="openDetailDialog(row)"
        ></tr>

        <tr class="mat-mdc-row" *matNoDataRow>
          <td
            class="mat-mdc-cell mdc-data-table__cell border-none!"
            [colSpan]="displayedColumns.length"
          >
            Geen resultaten gevonden
          </td>
        </tr>
      </table>

      <mat-paginator
        class="border-t border-(--mat-table-row-item-outline-color)"
        (page)="handlePageChange($event)"
        [pageSize]="pageSize()"
        [pageSizeOptions]="[25, 50, 100]"
        showFirstLastButtons
      ></mat-paginator>
    </app-data-table-container>
  </div>
</div>
